import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../shared/store/auth.store";
import { useProfileStore } from "../../shared/store/profile.store";
import { useDevicesStore } from "../../shared/store/devices.store";
import { useCompaniesStore } from "../../shared/store/companies.store";
import { ConfigRoutes, getPath } from "../../routes/routes.path";
import { CompaniesService } from "../../shared/services/companies.service";
import { DevicesService } from "../../shared/services/devices.service";
import { ButtonLarge, PopUp } from "../../shared/uikit";

const STATUS_STYLES: Record<string, { icon: string }> = {
  pending: { icon: "🟡" },
  approved: { icon: "🟢" },
  rejected: { icon: "🔴" },
  in_progress: { icon: "🔵" },
};

const getStatusStyle = (status: string) =>
  STATUS_STYLES[status] ?? {
    bg: "var(--second-background)",
    color: "var(--second-color)",
    icon: "⚪",
  };

export const ProfilePage = () => {
  const navigate = useNavigate();
  const { firstName, lastName, avatar } = useProfileStore.getState();
  const { instanceId } = useAuthStore.getState();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState<number | null>(null);

  // Companies state — use granular selectors to avoid infinite re-render loops
  const companies = useCompaniesStore((s) => s.companies);
  const companiesLoading = useCompaniesStore((s) => s.loading);
  const companiesError = useCompaniesStore((s) => s.error);

  // Devices state — not directly reactive here, only used for actions via getState()

  const fetchCompanies = useCallback(async (force = false) => {
    const { setLoading, setError, setCompanies, hasLoaded } =
      useCompaniesStore.getState();
    // При повторном монтировании страницы не дублируем GET /companies,
    // если данные уже загружены (кнопка Refresh форсирует перезагрузку).
    if (hasLoaded && !force) return;
    setLoading(true);
    setError(null);
    try {
      const result = await CompaniesService.getCompanies();
      setCompanies(result);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load companies";
      setError(message);
    }
  }, []);

  const fetchDevices = useCallback(async () => {
    const { setLoading, setError, setDevices } = useDevicesStore.getState();
    setLoading(true);
    setError(null);
    try {
      const result = await DevicesService.getDevices();
      setDevices(result);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load devices";
      setError(message);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
    fetchDevices();
  }, [fetchCompanies, fetchDevices]);

  const handleLeaveCompany = useCallback(
    async (companyId: number) => {
      try {
        await CompaniesService.leaveCompany(companyId);
        // Refresh companies list after leaving
        fetchCompanies(true);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to leave company";
        console.error("[ProfilePage] Leave company error:", message);
      }
      setShowLeaveModal(null);
    },
    [fetchCompanies],
  );

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    const { logout } = useAuthStore.getState();
    const { reset } = useProfileStore.getState();
    const { clearDevices } = useDevicesStore.getState();
    const { clearCompanies } = useCompaniesStore.getState();

    logout();
    reset();
    clearDevices();
    clearCompanies();

    localStorage.removeItem("onboarding_completed");
    localStorage.removeItem("cryptcloud-auth-storage");
    localStorage.removeItem("cryptcloud-profile-storage");

    setShowLogoutModal(false);
    navigate("/login", { replace: true });
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  return (
    <div style={{ padding: "1rem" }}>
      <h2>Profile</h2>

      {/* Profile Section */}
      <div
        style={{
          marginTop: "1rem",
          padding: "1rem",
          border: "1px solid var(--border-color)",
          borderRadius: "12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          {avatar ? (
            <img
              src={avatar}
              alt="Avatar"
              style={{ width: "48px", height: "48px", borderRadius: "50%" }}
            />
          ) : (
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                background: "var(--second-background)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "24px",
              }}
            >
              👤
            </div>
          )}
          <div>
            <strong style={{ fontSize: "16px" }}>
              {firstName} {lastName}
            </strong>
            <p
              style={{
                color: "var(--second-color)",
                fontSize: "14px",
                margin: "0.25rem 0 0",
              }}
            >
              Instance: {instanceId ?? "N/A"}
            </p>
          </div>
        </div>
      </div>

      {/* Companies Section */}
      <div
        style={{
          marginTop: "1rem",
          padding: "1rem",
          border: "1px solid var(--border-color)",
          borderRadius: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h3 style={{ margin: "0 0 0.5rem" }}>My Companies</h3>
          <ButtonLarge
            title="🔄 Refresh"
            onClick={() => fetchCompanies(true)}
          />
        </div>

        {companiesLoading ? (
          <div
            style={{
              textAlign: "center",
              padding: "1rem",
              color: "var(--second-color)",
            }}
          >
            <p>Loading companies...</p>
          </div>
        ) : companiesError ? (
          <div
            style={{
              textAlign: "center",
              padding: "1rem",
              color: "var(--accent-color)",
            }}
          >
            <p>{companiesError}</p>
            <ButtonLarge
              title="🔄 Retry"
              onClick={() => fetchCompanies(true)}
              mode="black"
              isSmall={true}
            />
          </div>
        ) : companies.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "1rem",
              border: "1px dashed var(--color-border)",
              borderRadius: "var(--radius-md)",
            }}
          >
            <p style={{ color: "var(--second-color)" }}>
              No companies registered yet.
            </p>
            <ButtonLarge
              title="🏢 Register New Company"
              onClick={() => navigate(getPath(ConfigRoutes.COMPANY_REGISTER))}
              mode="black"
            />
          </div>
        ) : (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
          >
            {companies.map((company) => {
              const statusStyle = getStatusStyle(company.status);
              const isAdmin = company.role === "admin";
              return (
                <div
                  key={company.id}
                  onClick={() => {
                    if (isAdmin) {
                      navigate(
                        getPath(ConfigRoutes.COMPANY_DETAIL, {
                          id: company.id,
                        }),
                      );
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "0.75rem 1rem",
                    background: "var(--first-background)",
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)",
                    cursor: isAdmin ? "pointer" : "default",
                    gap: "0.75rem",
                  }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "20px",
                      flexShrink: 0,
                      border: "1px solid var(--color-border)",
                      overflow: "hidden",
                    }}
                  >
                    {company.logo ? (
                      <img
                        src={company.logo}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      "🏢"
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: "14px", display: "block" }}>
                      {company.name}
                    </strong>
                    <span
                      style={{ fontSize: "12px", color: "var(--second-color)" }}
                    >
                      INN: {company.inn}
                    </span>
                  </div>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: "var(--radius-sm)",
                      fontSize: "11px",
                      fontWeight: 600,
                    }}
                  >
                    {isAdmin ? "👑 " : "👤 "}
                    {company.role}
                  </span>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: "var(--radius-sm)",
                      fontSize: "11px",
                      fontWeight: 600,
                    }}
                  >
                    {company.status.replace("_", " ")} {statusStyle.icon}
                  </span>
                  {!isAdmin && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowLeaveModal(company.id);
                      }}
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "16px",
                        padding: "4px 8px",
                        borderRadius: "var(--radius-sm)",
                        color: "var(--accent-color)",
                      }}
                    >
                      ❌ Leave
                    </button>
                  )}
                </div>
              );
            })}
            <ButtonLarge
              onClick={() => navigate(getPath(ConfigRoutes.COMPANY_REGISTER))}
              title="Register new Company"
            />
          </div>
        )}
      </div>

      {/* Leave Company Confirmation Modal */}
      {showLeaveModal !== null && (
        <PopUp
          content={
            <div style={{ textAlign: "center" }}>
              <h3 style={{ margin: "0 0 0.5rem" }}>Leave Company?</h3>
              <p
                style={{
                  color: "var(--second-color)",
                  fontSize: "14px",
                  lineHeight: "1.5",
                }}
              >
                Are you sure you want to leave this company? You will lose
                access to all company data.
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  marginTop: "1.5rem",
                  justifyContent: "center",
                }}
              >
                <ButtonLarge
                  title="Cancel"
                  onClick={() => setShowLeaveModal(null)}
                />
                <ButtonLarge
                  title="Leave"
                  onClick={() => handleLeaveCompany(showLeaveModal)}
                  Accent={true}
                />
              </div>
            </div>
          }
          isCloseButton={true}
          onClick={() => setShowLeaveModal(null)}
        />
      )}

      {/* App Info */}
      <div
        style={{
          marginTop: "1rem",
          padding: "1rem",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius-md)",
        }}
      >
        <h3 style={{ margin: "0 0 0.5rem" }}>App Info</h3>
        <p
          style={{ color: "var(--second-color)", fontSize: "14px", margin: 0 }}
        >
          CryptCloud MiniApp v0.1.0
        </p>
      </div>

      {/* Logout */}
      <ButtonLarge title="Logout" onClick={handleLogout} />

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <PopUp
          content={
            <div style={{ textAlign: "center" }}>
              <h3 style={{ margin: "0 0 0.5rem" }}>Confirm Logout</h3>
              <p
                style={{
                  color: "var(--second-color)",
                  fontSize: "14px",
                  lineHeight: "1.5",
                }}
              >
                Are you sure you want to log out? You will need to
                re-authenticate to access your account.
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  marginTop: "1.5rem",
                  justifyContent: "center",
                }}
              >
                <ButtonLarge title="Cancel" onClick={cancelLogout} />
                <ButtonLarge
                  title="Logout"
                  onClick={confirmLogout}
                  Accent={true}
                />
              </div>
            </div>
          }
          isCloseButton={true}
          onClick={cancelLogout}
        />
      )}
    </div>
  );
};
